import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Settings,
  Building2,
  Clock,
  DollarSign,
  Bell,
  Shield,
  Globe,
  Palette,
  ChevronLeft,
  Save,
  Upload
} from 'lucide-react';
import { clinicManagementApi, getLocalizedValue } from '@/services/clinicApi';

const ClinicSettings = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [settings, setSettings] = useState({
    name: { en: '', ar: '' },
    description: { en: '', ar: '' },
    phone: '',
    email: '',
    address: '',
    website: '',
    // Appointment Settings
    appointmentDuration: 30,
    appointmentBuffer: 5,
    maxDailyAppointments: 50,
    allowOnlineBooking: true,
    allowWalkIns: true,
    requirePaymentUpfront: false,
    // Notification Settings
    sendReminders: true,
    reminderHoursBefore: 24,
    // Financial Settings
    currency: 'SYP',
    taxRate: 0,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSettings();
  }, [isAuthenticated, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await clinicManagementApi.getClinics();
      if (response?.data?.data?.[0]) {
        const clinic = response.data.data[0];
        setSettings({
          ...settings,
          name: clinic.name || { en: '', ar: '' },
          description: clinic.description || { en: '', ar: '' },
          phone: clinic.phone_number || '',
          email: clinic.email || '',
          address: clinic.address || '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save settings via API
      toast.success(lang === 'ar' ? 'تم حفظ الإعدادات' : 'Settings saved successfully');
    } catch (error) {
      toast.error(lang === 'ar' ? 'فشل في حفظ الإعدادات' : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinic-settings">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'إعدادات العيادة' : 'Clinic Settings'}
                </h1>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              <Save className="h-4 w-4 mr-2" />
              {saving ? (lang === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (lang === 'ar' ? 'حفظ' : 'Save')}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">{lang === 'ar' ? 'عام' : 'General'}</span>
            </TabsTrigger>
            <TabsTrigger value="appointments" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">{lang === 'ar' ? 'المواعيد' : 'Appointments'}</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">{lang === 'ar' ? 'الإشعارات' : 'Notifications'}</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">{lang === 'ar' ? 'الفواتير' : 'Billing'}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>{lang === 'ar' ? 'معلومات العيادة' : 'Clinic Information'}</CardTitle>
                <CardDescription>
                  {lang === 'ar' ? 'المعلومات الأساسية للعيادة' : 'Basic clinic information'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'اسم العيادة (العربية)' : 'Clinic Name (Arabic)'}</Label>
                    <Input
                      value={settings.name.ar}
                      onChange={(e) => setSettings({ ...settings, name: { ...settings.name, ar: e.target.value } })}
                      dir="rtl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'اسم العيادة (الإنجليزية)' : 'Clinic Name (English)'}</Label>
                    <Input
                      value={settings.name.en}
                      onChange={(e) => setSettings({ ...settings, name: { ...settings.name, en: e.target.value } })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{lang === 'ar' ? 'الهاتف' : 'Phone'}</Label>
                  <Input
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{lang === 'ar' ? 'العنوان' : 'Address'}</Label>
                  <Textarea
                    value={settings.address}
                    onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="appointments" className="mt-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>{lang === 'ar' ? 'إعدادات المواعيد' : 'Appointment Settings'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'مدة الموعد (دقائق)' : 'Appointment Duration (minutes)'}</Label>
                    <Input
                      type="number"
                      value={settings.appointmentDuration}
                      onChange={(e) => setSettings({ ...settings, appointmentDuration: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'فترة الراحة (دقائق)' : 'Buffer Time (minutes)'}</Label>
                    <Input
                      type="number"
                      value={settings.appointmentBuffer}
                      onChange={(e) => setSettings({ ...settings, appointmentBuffer: parseInt(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{lang === 'ar' ? 'أقصى عدد مواعيد يومياً' : 'Max Daily Appointments'}</Label>
                  <Input
                    type="number"
                    value={settings.maxDailyAppointments}
                    onChange={(e) => setSettings({ ...settings, maxDailyAppointments: parseInt(e.target.value) })}
                  />
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{lang === 'ar' ? 'الحجز الإلكتروني' : 'Online Booking'}</p>
                      <p className="text-sm text-slate-500">{lang === 'ar' ? 'السماح للمرضى بالحجز عبر الإنترنت' : 'Allow patients to book online'}</p>
                    </div>
                    <Switch
                      checked={settings.allowOnlineBooking}
                      onCheckedChange={(checked) => setSettings({ ...settings, allowOnlineBooking: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{lang === 'ar' ? 'الزيارات بدون موعد' : 'Walk-ins'}</p>
                      <p className="text-sm text-slate-500">{lang === 'ar' ? 'قبول الزيارات بدون موعد مسبق' : 'Accept walk-in patients'}</p>
                    </div>
                    <Switch
                      checked={settings.allowWalkIns}
                      onCheckedChange={(checked) => setSettings({ ...settings, allowWalkIns: checked })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="mt-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>{lang === 'ar' ? 'إعدادات الإشعارات' : 'Notification Settings'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{lang === 'ar' ? 'تذكيرات الموعد' : 'Appointment Reminders'}</p>
                    <p className="text-sm text-slate-500">{lang === 'ar' ? 'إرسال تذكيرات للمرضى قبل الموعد' : 'Send reminders to patients before appointments'}</p>
                  </div>
                  <Switch
                    checked={settings.sendReminders}
                    onCheckedChange={(checked) => setSettings({ ...settings, sendReminders: checked })}
                  />
                </div>

                {settings.sendReminders && (
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'إرسال التذكير قبل (ساعات)' : 'Send reminder before (hours)'}</Label>
                    <Input
                      type="number"
                      value={settings.reminderHoursBefore}
                      onChange={(e) => setSettings({ ...settings, reminderHoursBefore: parseInt(e.target.value) })}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="mt-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>{lang === 'ar' ? 'إعدادات الفواتير' : 'Billing Settings'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'العملة' : 'Currency'}</Label>
                    <Select
                      value={settings.currency}
                      onValueChange={(value) => setSettings({ ...settings, currency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SYP">{lang === 'ar' ? 'ليرة سورية' : 'Syrian Pound (SYP)'}</SelectItem>
                        <SelectItem value="USD">{lang === 'ar' ? 'دولار أمريكي' : 'US Dollar (USD)'}</SelectItem>
                        <SelectItem value="EUR">{lang === 'ar' ? 'يورو' : 'Euro (EUR)'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'نسبة الضريبة (%)' : 'Tax Rate (%)'}</Label>
                    <Input
                      type="number"
                      value={settings.taxRate}
                      onChange={(e) => setSettings({ ...settings, taxRate: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{lang === 'ar' ? 'الدفع المسبق' : 'Upfront Payment'}</p>
                    <p className="text-sm text-slate-500">{lang === 'ar' ? 'طلب الدفع عند الحجز' : 'Require payment when booking'}</p>
                  </div>
                  <Switch
                    checked={settings.requirePaymentUpfront}
                    onCheckedChange={(checked) => setSettings({ ...settings, requirePaymentUpfront: checked })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClinicSettings;
